import { Button as KendoButton } from "@tql/ui-components-react/kendo/buttons";
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { refreshCcw_03 } from "@tql/ui-components-react/untitled/icons";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import type { ZodSchema } from "zod";

import { UserFilterMultiselect } from "~/features/quote-board/quote-board-header/user-filter-multiselect.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { useSharedUserFilter } from "~/shared/hooks/use-shared-user-filter.ts";
import {
	customerSchema,
	laeSchema,
	ltlOwnerSchema,
} from "~/shared/schemas/customer-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";

import { AccountsReceivableCard } from "./components/accounts-receivable-card/accounts-receivable-card.tsx";
import { CarriersCard } from "./components/carriers-card/carriers-card.tsx";
import { ClaimsCard } from "./components/claims-card/claims-card.tsx";
import { SalesPerformanceCard } from "./components/sales-performance-card/sales-performance-card.tsx";
import type { LoadsNavigationFilters } from "./components/shipment-management-card/shipment-management-card.tsx";
import { ShipmentManagementCard } from "./components/shipment-management-card/shipment-management-card.tsx";
import type { DateRange } from "./components/week-selector/week-selector.tsx";
import {
	getDateRange,
	WeekSelector,
} from "./components/week-selector/week-selector.tsx";
import { useCarriers } from "./hooks/use-carriers.ts";
import { useClaims } from "./hooks/use-claims.ts";
import { useQuoteSummary } from "./hooks/use-quote-summary.ts";
import { useSalesPerformance } from "./hooks/use-sales-performance.ts";
import { useShipmentManagement } from "./hooks/use-shipment-management.ts";
import styles from "./index.module.css";
import type { SalesPerformance } from "./schemas/dashboard-schemas.ts";
import {
	markQuoteMetricsUnavailable,
	mergeQuoteSummary,
} from "./utils/merge-quote-summary.ts";

const emptySalesPerformance: SalesPerformance = {
	rows: [],
	totalBrokerageRevenue: 0,
};

type ArUserFilter = {
	id: number;
	name: string;
	endpoint: string;
	schema: ZodSchema<unknown> | null;
	paramType: string;
};

const arUserFilters: ArUserFilter[] = [
	{
		id: 1,
		name: "LTL AR",
		endpoint: apiRoutes.GETLTLBROKERS,
		schema: ltlOwnerSchema.array(),
		paramType: "ltlBrokers",
	},
	{
		id: 2,
		name: "LAE",
		endpoint: apiRoutes.GETPRIMARYBROKERS,
		schema: laeSchema.array(),
		paramType: "laes",
	},
	{
		id: 3,
		name: "Customer",
		endpoint: apiRoutes.CUSTOMERSEARCH,
		schema: customerSchema.array(),
		paramType: "customers",
	},
];

export const ArDashboard = () => {
	const { showError } = useNotificationActions();
	const {
		selectedFilter: selectedUserFilter,
		value: userFilterValue,
		ltlBrokerIds,
		primaryBrokerIds,
		customerIds,
		handleChange: handleUserFilterChange,
		handleSelectFilter: handleSelectUserFilter,
	} = useSharedUserFilter(arUserFilters);

	const [dateRange, setDateRange] = useState<DateRange>(() =>
		getDateRange("thisWeek"),
	);
	const [selectedSalesCustomerId, setSelectedSalesCustomerId] = useState<
		number | null
	>(null);
	const [selectedCarrierId, setSelectedCarrierId] = useState<number | null>(
		null,
	);
	const [carrierSearch, setCarrierSearch] = useState("");
	const [debouncedCarrierSearch] = useDebounceValue(carrierSearch, 300);

	const sharedParams = { ltlBrokerIds, primaryBrokerIds, customerIds };
	const dateParams = {
		startDate: dateRange.start,
		endDate: dateRange.end,
	};

	const loadsNavigationFilters: LoadsNavigationFilters = useMemo(() => {
		const transferCustomers =
			selectedUserFilter?.paramType === "customers"
				? userFilterValue.map((v) => ({
						id: Number(v.id),
						name: v.name,
					}))
				: [];
		return {
			customers: transferCustomers,
			ltlBrokerIds,
			primaryBrokerIds,
		};
	}, [selectedUserFilter, userFilterValue, ltlBrokerIds, primaryBrokerIds]);
	const [lastRefreshed, setLastRefreshed] = useState<Date>(() => new Date());
	const {
		data: shipmentManagement,
		isLoading: isLoadingShipmentManagement,
		isFetching: isFetchingShipmentManagement,
		refetch: refetchShipmentManagement,
	} = useShipmentManagement(sharedParams);
	const {
		data: claims,
		isLoading: isLoadingClaims,
		isFetching: isFetchingClaims,
		refetch: refetchClaims,
	} = useClaims(sharedParams);
	const {
		data: salesPerformance,
		isLoading: isLoadingSalesPerformance,
		isFetching: isFetchingSalesPerformance,
		isSuccess: isSalesPerformanceSuccess,
		error: salesPerformanceError,
		refetch: refetchSalesPerformance,
	} = useSalesPerformance({
		...sharedParams,
		...dateParams,
		carrierId: selectedCarrierId,
	});
	const {
		data: quoteSummary,
		isLoading: isLoadingQuoteSummary,
		isFetching: isFetchingQuoteSummary,
		isEnabled: isQuoteSummaryEnabled,
		isSuccess: isQuoteSummarySuccess,
		error: quoteSummaryError,
		refetch: refetchQuoteSummary,
	} = useQuoteSummary({
		...sharedParams,
		...dateParams,
		carrierId: selectedCarrierId,
	});
	const isQuoteSummaryAuthoritative =
		isQuoteSummaryEnabled && isQuoteSummarySuccess;
	const salesPerformanceWithQuotes = useMemo(() => {
		if (!isSalesPerformanceSuccess) {
			return emptySalesPerformance;
		}

		return isQuoteSummaryAuthoritative
			? mergeQuoteSummary(salesPerformance, quoteSummary)
			: markQuoteMetricsUnavailable(salesPerformance);
	}, [
		isQuoteSummaryAuthoritative,
		isSalesPerformanceSuccess,
		quoteSummary,
		salesPerformance,
	]);
	const hasShownSalesPerformanceErrorRef = useRef(false);
	useEffect(() => {
		if (isFetchingSalesPerformance) {
			hasShownSalesPerformanceErrorRef.current = false;
			return;
		}

		if (salesPerformanceError) {
			if (!hasShownSalesPerformanceErrorRef.current) {
				showError("Failed to load Sales Performance. Please try again.");
				hasShownSalesPerformanceErrorRef.current = true;
			}
		} else {
			hasShownSalesPerformanceErrorRef.current = false;
		}
	}, [isFetchingSalesPerformance, salesPerformanceError, showError]);
	const hasShownQuoteSummaryErrorRef = useRef(false);
	useEffect(() => {
		if (isFetchingQuoteSummary) {
			hasShownQuoteSummaryErrorRef.current = false;
			return;
		}

		if (quoteSummaryError) {
			if (!hasShownQuoteSummaryErrorRef.current) {
				showError("Failed to load Quotes and SQ Ratio. Please try again.");
				hasShownQuoteSummaryErrorRef.current = true;
			}
		} else {
			hasShownQuoteSummaryErrorRef.current = false;
		}
	}, [isFetchingQuoteSummary, quoteSummaryError, showError]);
	const {
		data: carriers,
		isLoading: isLoadingCarriers,
		isFetching: isFetchingCarriers,
		refetch: refetchCarriers,
	} = useCarriers({
		...sharedParams,
		...dateParams,
		selectedCustomerId: selectedSalesCustomerId,
		search: debouncedCarrierSearch || null,
	});

	const handleRefresh = async () => {
		const refreshResults = await Promise.all([
			refetchShipmentManagement(),
			refetchClaims(),
			refetchSalesPerformance(),
			refetchQuoteSummary(),
			refetchCarriers(),
		]);

		if (
			refreshResults.every(
				(result) => result?.error === undefined || result.error === null,
			)
		) {
			setLastRefreshed(new Date());
		}
	};

	return (
		<div className={styles.arDashboardWrapper}>
			<TraxPageHeader title={"AR Dashboard"} />
			<div className={styles.arDashboardContainer}>
				<div className={styles.arDashboardHeader}>
					<div className={styles.arDashboardUserSelect}>
						<UserFilterMultiselect
							onHandleChange={handleUserFilterChange}
							onHandleSelectFilter={handleSelectUserFilter}
							selectedFilter={selectedUserFilter}
							selectedValue={userFilterValue}
							showLabel={false}
							userFilters={arUserFilters}
						/>
					</div>
					<div>
						<span className={styles.arDashboardLastRefreshed}>
							{`Last refreshed ${format(lastRefreshed, "M/d/yyyy, h:mm a")}`}
						</span>
					</div>

					<KendoButton
						fillMode={"link"}
						onClick={() => void handleRefresh()}
						size={"large"}
						themeColor={"primary"}
					>
						<SvgIcon icon={refreshCcw_03} size={"xxlarge"} />
					</KendoButton>
				</div>
				<div className={styles.arDashboardSectionContainer}>
					<div className={styles.arDashboardSection}>
						<ShipmentManagementCard
							data={shipmentManagement}
							isFetching={isFetchingShipmentManagement}
							isLoading={isLoadingShipmentManagement}
							loadsNavigationFilters={loadsNavigationFilters}
						/>
						<ClaimsCard
							data={claims}
							isFetching={isFetchingClaims}
							isLoading={isLoadingClaims}
						/>
						<AccountsReceivableCard />
					</div>
					<div
						className={`${styles.arDashboardSection} ${styles.arDashboardSectionRight}`}
					>
						<WeekSelector
							onRangeChange={(range) => {
								setDateRange(range);
							}}
						/>
						<SalesPerformanceCard
							data={salesPerformanceWithQuotes}
							isFetching={isFetchingSalesPerformance || isFetchingQuoteSummary}
							isLoading={isLoadingSalesPerformance || isLoadingQuoteSummary}
							isQuoteSummaryUnavailable={
								!isQuoteSummaryAuthoritative || !isSalesPerformanceSuccess
							}
							isSalesPerformanceUnavailable={!isSalesPerformanceSuccess}
							onCustomerSelect={setSelectedSalesCustomerId}
							selectedCustomerId={selectedSalesCustomerId}
						/>
						<CarriersCard
							data={carriers}
							isFetching={isFetchingCarriers}
							isLoading={isLoadingCarriers}
							onCarrierSelect={setSelectedCarrierId}
							onSearchChange={setCarrierSearch}
							searchValue={carrierSearch}
							selectedCarrierId={selectedCarrierId}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
